// #2140 (docs/score/announcer-ui-redesign.md §4.2) — band 2, "the day": a
// non-scrolling vertical spine of the selected ring's CLASSES (not rides),
// one day at a time, in running order.
//
// Pure functions only. Reuses buildOrderOfGoRows (orderOfGo.js) for the
// time-sort/class-header/day-key derivation rather than re-deriving it —
// that function already merges rides+breaks into one time-ordered list and
// inserts a CLASS_HEADER row before a class's first ride, tagging every row
// with its venue-local dayKey. Called here with roster args at their
// defaults ([]) since the spine never shows rider/horse names, only class
// number/name and a per-class tally.

import { RIDE_STATUSES } from "score-common";
import { buildOrderOfGoRows, ROW_KIND } from "./orderOfGo.js";
import { venueDayKey } from "./formatTime.js";

export const SPINE_MARKER = {
  DONE: "DONE",
  RUNNING: "RUNNING",
  TO_COME: "TO_COME",
};

// A scratch has gone through the ring even though it never scored — count it
// as ridden. ENTERING_RING/IN_PROGRESS have not (score-common's RIDE_STATUSES).
const RIDDEN_STATUSES = new Set([RIDE_STATUSES.COMPLETED, RIDE_STATUSES.SCRATCHED]);

// #2140 judge defect 2 — an item with no derivable day (no scheduledTime at
// all, e.g. a #386 emergency manual ride entered off-daysheet, or an undated
// break) is bucketed onto TODAY ONLY, not kept on every day the way
// RingDataContext's showAllDays filter keeps it (#892, `!day || ...`). That
// convention is correct for a two-state today/all toggle, where duplicating
// an undated item into "today" costs nothing — but it does NOT transfer to
// an arbitrary N-day switcher: kept on every day, an undated ride/break would
// be double-counted in every day's footer and render a phantom row on days
// it was never meant to occupy. Bucketing it onto today keeps it counted
// exactly once, and (per #386) still visible rather than silently dropped.
function filterToDay(items, dayKey, todayKey) {
  return (items || []).filter((item) => {
    const day = venueDayKey(item.scheduledTime, item.scheduledTimeZone);
    if (day) return day === dayKey;
    return dayKey === todayKey;
  });
}

/**
 * Build the day spine's class rows for ONE venue-local day.
 *
 * @param {object} args
 * @param {Array} args.rides - ALL of the ring's rides (every day) — the
 *   function itself scopes to `dayKey`; do not pre-filter by day, breaks'
 *   duration inference needs the full unfiltered set.
 * @param {Array} args.breaks - ALL of the ring's breaks (every day).
 * @param {Array} args.classes
 * @param {object} args.statuses - rideId -> live status, same shape
 *   buildOrderOfGoRows already accepts.
 * @param {string|null} args.liveClassId - RUNNING marker; takes priority
 *   over the ridden tally regardless of counts (ring truth is authoritative).
 * @param {(classId: string) => boolean} args.isClassPlacedFromSummary
 * @param {string} args.dayKey - the venue-local day currently shown
 *   ("YYYY-MM-DD"). Required — callers resolve the default (today).
 * @param {string} [args.todayKey] - the venue-local day AS OF NOW
 *   ("YYYY-MM-DD"), distinct from `dayKey` (the shown day). Undated
 *   rides/breaks bucket onto this day only (see filterToDay above) —
 *   defaults to `dayKey` when omitted, so a caller that never distinguishes
 *   "shown" from "today" (e.g. a test with only one day in play) still gets
 *   exactly-once counting rather than an undefined-vs-undefined mismatch.
 * @returns {{ entries: Array }} class rows plus the day's break rows in time
 *   order (#3449; #3392's no-footer, no-countdown rules still stand).
 */
export function buildDaySpine({
  rides = [],
  breaks = [],
  classes = [],
  statuses = {},
  liveClassId = null,
  isClassPlacedFromSummary = () => false,
  dayKey,
  todayKey = dayKey,
} = {}) {
  const dayRides = filterToDay(rides, dayKey, todayKey);
  const dayBreaks = filterToDay(breaks, dayKey, todayKey);

  const dayRows = buildOrderOfGoRows({
    rides: dayRides,
    breaks: dayBreaks,
    classes,
    statuses,
  });

  // Tally ridesTotal/ridesRidden per classId ONCE, scoped to the shown day
  // (shown day only, not the whole show, AC13).
  const tallyByClassId = new Map();
  for (const row of dayRows) {
    if (row.kind !== ROW_KIND.RIDE || !row.classId) continue;
    const tally = tallyByClassId.get(row.classId) || { total: 0, ridden: 0 };
    tally.total += 1;
    if (RIDDEN_STATUSES.has(row.status)) tally.ridden += 1;
    tallyByClassId.set(row.classId, tally);
  }

  const entries = [];

  for (const row of dayRows) {
    if (row.kind === ROW_KIND.CLASS_HEADER) {
      const tally = tallyByClassId.get(row.classId) || { total: 0, ridden: 0 };
      const isRunning = Boolean(liveClassId) && row.classId === liveClassId;
      const isDone = !isRunning && tally.total > 0 && tally.ridden === tally.total;
      const marker = isRunning
        ? SPINE_MARKER.RUNNING
        : isDone
          ? SPINE_MARKER.DONE
          : SPINE_MARKER.TO_COME;
      const placed = isClassPlacedFromSummary(row.classId);
      entries.push({
        kind: "CLASS",
        id: row.id,
        classId: row.classId,
        classNumber: row.classNumber,
        className: row.className,
        dayKey: row.dayKey,
        marker,
        isRunning,
        isDone,
        placed,
        ridesTotal: tally.total,
        ridesRidden: tally.ridden,
      });
    }
    // #3449: a break is an entry again (reverses part of #3392), in time order
    // between the class rows (buildOrderOfGoRows already sorted them). It
    // carries no countdown and is not selectable. RIDE rows never become
    // entries; they only feed the tally above.
    if (row.kind === ROW_KIND.BREAK) {
      entries.push({
        kind: "BREAK",
        id: row.id,
        label: row.label,
        scheduledTime: row.scheduledTime,
        scheduledTimeZone: row.scheduledTimeZone,
        dayKey: row.dayKey,
      });
    }
  }

  return { entries };
}

// Move a "YYYY-MM-DD" day key by `deltaDays` whole calendar days. Anchored at
// noon local time (same trick formatTime.js's dayKeyLabel uses) so a DST
// transition can never shift the resulting calendar date by a day.
export function shiftDayKey(dayKey, deltaDays) {
  if (!dayKey) return dayKey;
  const d = new Date(`${dayKey}T12:00:00`);
  if (Number.isNaN(d.getTime())) return dayKey;
  d.setDate(d.getDate() + deltaDays);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// "Fri, Sep 12" from a venueDayKey ("2026-09-12") — the day switcher's own
// label. Same noon-anchor trick as shiftDayKey/dayKeyLabel above.
export function formatDaySwitcherLabel(dayKey) {
  if (!dayKey) return "";
  const d = new Date(`${dayKey}T12:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}
